import React from "react";
import { motion } from "framer-motion";
import { MessageSquare } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Badge } from "../GraderUI";
import ScoreGauge from "./ScoreGauge";

const EXAMPLES_CAP = 3;

const ESTIMATE_SUBTITLE =
	"Content-tone estimate — earn AI mentions (web search) to measure real brand sentiment";
const MEASURED_SUBTITLE =
	"How AI engines portray your brand in their answers";

const INLINE_MARKDOWN_COMPONENTS = {
	p: ({ children }) => <>{children}</>,
	a: ({ href, children }) => (
		<a href={href} target="_blank" rel="noopener noreferrer" className="sentiment-analysis__positive-link">
			{children}
		</a>
	),
	strong: ({ children }) => <strong>{children}</strong>,
	em: ({ children }) => <em>{children}</em>,
};

function formatProviderLabel(providerKey) {
	const key = String(providerKey || "");
	if (!key) return "";
	return key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
}

function SentimentScaleLegend() {
	return (
		<div className="sentiment-analysis__scale" aria-label="Sentiment scale">
			<div className="sentiment-analysis__scale-track" aria-hidden="true" />
			<div className="sentiment-analysis__scale-marks">
				<span className="sentiment-analysis__scale-mark">
					<strong>0</strong>
					<span>Very negatively portrayed</span>
				</span>
				<span className="sentiment-analysis__scale-mark sentiment-analysis__scale-mark--center">
					<strong>50</strong>
					<span>Neutral / factual</span>
				</span>
				<span className="sentiment-analysis__scale-mark sentiment-analysis__scale-mark--end">
					<strong>100</strong>
					<span>Very positively portrayed</span>
				</span>
			</div>
		</div>
	);
}

function SentimentSectionHeader({ isMeasured }) {
	return (
		<div className="sentiment-analysis__header">
			<div className="report-section__header sentiment-analysis__header-main">
				<div className="report-section__icon-wrap report-section__icon-wrap--green">
					<MessageSquare className="report-section__icon" />
				</div>
				<div className="report-section__header-text">
					<div className="sentiment-analysis__title-row">
						<h2 className="report-section__title sentiment-analysis__title">
							Sentiment &amp; Tone Analysis
						</h2>
						{isMeasured ? (
							<Badge
								variant="success"
								className="sentiment-analysis__mode-badge sentiment-analysis__mode-badge--measured"
							>
								Measured
							</Badge>
						) : (
							<Badge variant="warning" className="sentiment-analysis__mode-badge">
								AI-perceived estimate
							</Badge>
						)}
					</div>
					<p className="report-section__subtitle">
						{isMeasured ? MEASURED_SUBTITLE : ESTIMATE_SUBTITLE}
					</p>
				</div>
			</div>
			<SentimentScaleLegend />
		</div>
	);
}

function SentimentProviderCard({ provider, index, isMeasured }) {
	const score = Math.round(Number(provider.sentiment_score ?? 0));
	const mentions = Number(provider.mentions ?? 0);
	const label = formatProviderLabel(provider.provider);

	return (
		<motion.div
			className="sentiment-analysis__provider-card"
			initial={{ opacity: 0, y: 12 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.35, delay: index * 0.08 }}
		>
			<h4 className="sentiment-analysis__provider-name">{label}</h4>
			<ScoreGauge
				value={score}
				label={isMeasured ? undefined : "Brand sentiment"}
				sublabel={isMeasured ? undefined : "site-tone estimate"}
				index={index}
			/>
			{isMeasured && (
				<p className="sentiment-analysis__mentions">
					across {mentions} brand mention{mentions !== 1 ? "s" : ""}
				</p>
			)}
		</motion.div>
	);
}

function DistributionRow({ label, count, pct, modifier }) {
	return (
		<div className="sentiment-analysis__distribution-row">
			<span className="sentiment-analysis__distribution-label">{label}</span>
			<div className="sentiment-analysis__distribution-track">
				<div
					className={`sentiment-analysis__distribution-fill sentiment-analysis__distribution-fill--${modifier}`}
					style={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }}
				/>
			</div>
			<span className="sentiment-analysis__distribution-stat">
				{count} - {Math.round(pct)}%
			</span>
		</div>
	);
}

function SentimentDistributionPanel({ distribution }) {
	if (!distribution) return null;

	const positive = Number(distribution.positive ?? 0);
	const neutral = Number(distribution.neutral ?? 0);
	const negative = Number(distribution.negative ?? 0);
	const positivePct = Number(distribution.positive_pct ?? 0);
	const neutralPct = Number(distribution.neutral_pct ?? 0);
	const negativePct = Number(distribution.negative_pct ?? 0);
	const total = positive + neutral + negative;

	if (total === 0) return null;

	return (
		<div className="sentiment-analysis__panel-card">
			<h3 className="sentiment-analysis__section-title">How AI describes you</h3>
			<div className="sentiment-analysis__distribution-rows">
				<DistributionRow label="Positive" count={positive} pct={positivePct} modifier="positive" />
				<DistributionRow label="Neutral" count={neutral} pct={neutralPct} modifier="neutral" />
				<DistributionRow label="Negative" count={negative} pct={negativePct} modifier="negative" />
			</div>
		</div>
	);
}

function SentimentExamplesPanel({ examples = [], tone = "positive" }) {
	const items = (Array.isArray(examples) ? examples : []).slice(0, EXAMPLES_CAP);
	const isPositive = tone === "positive";
	const title = isPositive ? "What AI says positively" : "What AI says negatively";
	const emptyText = isPositive
		? "No positive mentions detected"
		: "No negative mentions detected";

	return (
		<div className="sentiment-analysis__panel-card">
			<h3
				className={`sentiment-analysis__section-title sentiment-analysis__section-title--${tone}`}
			>
				{title}
			</h3>
			{items.length > 0 ? (
				<ul className="sentiment-analysis__examples-list">
					{items.map((text, i) => (
						<li
							key={i}
							className={`sentiment-analysis__example-item sentiment-analysis__example-item--${tone}`}
						>
							<ReactMarkdown components={INLINE_MARKDOWN_COMPONENTS}>
								{text}
							</ReactMarkdown>
						</li>
					))}
				</ul>
			) : (
				<p className="sentiment-analysis__example-empty">{emptyText}</p>
			)}
		</div>
	);
}

function SentimentFooter({ consensusSummary, methodology }) {
	if (!consensusSummary && !methodology) return null;

	return (
		<div className="report-section__callout report-section__callout--green sentiment-analysis__footer">
			{consensusSummary && (
				<p className="report-section__callout-text">{consensusSummary}</p>
			)}
			{methodology && (
				<p className="sentiment-analysis__footer-methodology">{methodology}</p>
			)}
		</div>
	);
}

function MeasuredSentimentLayout({ report }) {
	const positiveExamples = report.positive_examples || [];
	const negativeExamples = report.negative_examples || [];
	const hasDetailsRow =
		Boolean(report.distribution) ||
		positiveExamples.length > 0 ||
		negativeExamples.length > 0;

	return (
		<>
			<SentimentSectionHeader isMeasured />

			{report.providers?.length > 0 && (
				<div className="sentiment-analysis__provider-grid">
					{report.providers.map((provider, index) => (
						<SentimentProviderCard
							key={provider.provider}
							provider={provider}
							index={index}
							isMeasured
						/>
					))}
				</div>
			)}

			{hasDetailsRow && (
				<div className="sentiment-analysis__measured-row">
					<SentimentDistributionPanel distribution={report.distribution} />
					<div className="sentiment-analysis__examples-stack">
						<SentimentExamplesPanel
							examples={positiveExamples}
							tone="positive"
						/>
						<SentimentExamplesPanel
							examples={negativeExamples}
							tone="negative"
						/>
					</div>
				</div>
			)}

			<SentimentFooter
				consensusSummary={report.consensus_summary}
				methodology={report.methodology}
			/>
		</>
	);
}

function EstimateSentimentLayout({ report }) {
	return (
		<>
			<SentimentSectionHeader isMeasured={false} />

			<div className="sentiment-analysis__provider-grid">
				{report.providers.map((provider, index) => (
					<SentimentProviderCard
						key={provider.provider}
						provider={provider}
						index={index}
						isMeasured={false}
					/>
				))}
			</div>

			{report.consensus_summary && (
				<div className="report-section__callout report-section__callout--green sentiment-analysis__footer">
					<p className="report-section__callout-text">{report.consensus_summary}</p>
				</div>
			)}
		</>
	);
}

function hasMeasuredContent(report) {
	return (
		(report.providers?.length ?? 0) > 0 ||
		report.distribution ||
		(report.positive_examples?.length ?? 0) > 0 ||
		(report.negative_examples?.length ?? 0) > 0 ||
		report.consensus_summary ||
		report.methodology
	);
}

export default function SentimentAnalysisSection({ report }) {
	if (!report) return null;

	const isMeasured = Boolean(report.is_measured);

	if (!isMeasured && !report.providers?.length) return null;
	if (isMeasured && !hasMeasuredContent(report)) return null;

	return (
		<motion.div
			className="report-section sentiment-analysis"
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5 }}
		>
			{isMeasured ? (
				<MeasuredSentimentLayout report={report} />
			) : (
				<EstimateSentimentLayout report={report} />
			)}
		</motion.div>
	);
}
